import WarehouseIcon from '@mui/icons-material/Warehouse';
import {
  createWarehouseSchema,
  type CreateWarehouseInput,
  type UpdateWarehouseInput,
  type Warehouse,
} from '@fdt/shared';
import { Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CrudPage } from '../components/CrudPage';
import type { Column } from '../components/DataTable';
import { StatusChip } from '../components/StatusChip';
import { warehousesApi } from '../resources';

interface FormState {
  code: string;
  name: string;
  address: string;
  lat: string;
  lng: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = { code: '', name: '', address: '', lat: '', lng: '', isActive: true };

export function WarehousesPage() {
  const { t } = useTranslation();

  const columns: Column<Warehouse>[] = [
    { id: 'code', label: t('fields.code'), render: (row) => row.code, sortable: true },
    { id: 'name', label: t('fields.name'), render: (row) => row.name, sortable: true },
    { id: 'address', label: t('fields.address'), render: (row) => row.address ?? '—' },
    {
      id: 'location',
      label: t('fields.location'),
      render: (row) =>
        row.location ? (
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {row.location.lat.toFixed(5)}, {row.location.lng.toFixed(5)}
          </Typography>
        ) : (
          // A warehouse without coordinates cannot be a route start or end point.
          <Chip size="small" variant="outlined" color="warning" label={t('common.missing')} />
        ),
    },
    {
      id: 'isActive',
      label: t('fields.status'),
      render: (row) => <StatusChip active={row.isActive} />,
    },
  ];

  return (
    <CrudPage<Warehouse, FormState, CreateWarehouseInput, UpdateWarehouseInput>
      title={t('nav.warehouses')}
      icon={WarehouseIcon}
      tone="neutral"
      resourceKey="warehouses"
      api={warehousesApi}
      columns={columns}
      rowKey={(row) => row.id}
      emptyForm={EMPTY_FORM}
      defaultSortBy="code"
      schema={createWarehouseSchema}
      toForm={(row) => ({
        code: row.code,
        name: row.name,
        address: row.address ?? '',
        lat: row.location ? String(row.location.lat) : '',
        lng: row.location ? String(row.location.lng) : '',
        isActive: row.isActive,
      })}
      toPayload={(form) => ({
        code: form.code,
        name: form.name,
        address: form.address || null,
        location:
          form.lat.trim() === ''
            ? null
            : { lat: Number(form.lat), lng: Number(form.lng) },
        isActive: form.isActive,
      })}
      // Half a coordinate pair is not a location, and zod sees only the object.
      validate={(form) =>
        (form.lat.trim() === '') !== (form.lng.trim() === '')
          ? { location: 'validation.bothCoordinates' }
          : null
      }
      createTitle={t('warehouses.createTitle')}
      editTitle={t('warehouses.editTitle')}
      deleteTitle={t('warehouses.deleteTitle')}
      deleteMessage={(row) => t('warehouses.deleteMessage', { name: row.name })}
      renderForm={({ form, setForm, fieldErrors }) => (
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('fields.code')}
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value })}
              error={Boolean(fieldErrors.code)}
              helperText={fieldErrors.code ? t(fieldErrors.code) : undefined}
              required
              fullWidth
            />
            <TextField
              label={t('fields.name')}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              error={Boolean(fieldErrors.name)}
              helperText={fieldErrors.name ? t(fieldErrors.name) : undefined}
              required
              fullWidth
            />
          </Stack>

          <TextField
            label={t('fields.address')}
            value={form.address}
            onChange={(event) => setForm({ ...form, address: event.target.value })}
            fullWidth
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Lat"
              value={form.lat}
              onChange={(event) => setForm({ ...form, lat: event.target.value })}
              error={Boolean(fieldErrors.location)}
              helperText={
                fieldErrors.location ? t(fieldErrors.location) : t('warehouses.locationHelp')
              }
              inputMode="decimal"
              fullWidth
            />
            <TextField
              label="Lng"
              value={form.lng}
              onChange={(event) => setForm({ ...form, lng: event.target.value })}
              error={Boolean(fieldErrors.location)}
              inputMode="decimal"
              fullWidth
            />
            <TextField
              select
              label={t('fields.status')}
              value={form.isActive ? 'true' : 'false'}
              onChange={(event) => setForm({ ...form, isActive: event.target.value === 'true' })}
              fullWidth
            >
              <MenuItem value="true">{t('common.active')}</MenuItem>
              <MenuItem value="false">{t('common.inactive')}</MenuItem>
            </TextField>
          </Stack>
        </Stack>
      )}
    />
  );
}
